/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack, Typography } from '@mui/material';
import {
  ScheduleComponent,
  Inject,
  ViewDirective,
  ViewsDirective,
  RenderCellEventArgs,
  ResourceDirective,
  ResourcesDirective,
  Resize,
  DragAndDrop,
  Day,
  TimelineViews,
  View,
} from '@syncfusion/ej2-react-schedule';
import { useDispatch, useSelector } from 'react-redux';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { StudioInterface } from 'packages/blu-booking/src/interfaces/Studio';
import { RootState } from 'packages/blu-booking/src/store';
import { getConfig } from '../../../utility_objects/WhiteCache';
import {
  AppointmentMouseDown,
  AppointmentMouseUp,
} from '../../../utility_objects/AppointmentMouseUp';
import { CalendarEventTemplate } from '../../../UI/CalendarEventTemplate/CalendarEventTemplate';
import { CalendarEventQuickInfo } from '../../../UI/CalendarEventQuickInfo/CalendarEventQuickInfo';
import { AppointmentRender } from '../../../utility_objects/AppointmentRender';
import { AppointmentResize } from '../../../utility_objects/AppointmentResize';
import { AppointmentDragDrop } from '../../../utility_objects/AppointmentDragDrop';
import { appointmentFormPopupOpen } from '../../../utility_objects/AppointmentFormPopupOpen';
import { StudioCalendarSetSelectedDate } from '../store';
import { useSchedulerPersistence } from '../../../hooks/useSchedulerPersistence';
import { useSchedulerLayoutRefresh } from '../../../hooks/useSchedulerLayoutRefresh';
import { CalendarLoader } from '../../../UI/CalendarLoader/CalendarLoader';
import {
  CellStatus,
  WorkHoursCellTemplate,
} from '../../../UI/WorkHoursCellTemplate/WorkHoursCellTemplate';
import { formatOraSyncfusion } from '../../../hooks/useFinestraOraria';

interface SchedulerProps {
  calendarPointOfSale: any;
  shiftPointOfSale: any;
  studi: StudioInterface[];
  /**
   * Ore mostrate in Verticale e in Orizzontale, intere (useFinestraOraria in
   * StudioCalendarInner): 5-24 con Ore «Tutte» nel riquadro Visualizzazione
   */
  startHour: number;
  endHour: number;
}

export const Scheduler = memo(
  ({
    calendarPointOfSale,
    shiftPointOfSale,
    studi,
    startHour,
    endHour,
  }: SchedulerProps) => {
    const { appointments } = useSelector(
      (state: RootState) => state.bluBookingaAppointments
    );
    const {
      selectedDate,
      selectedPointOfSale,
      selectedUsers,
      selectedServices,
      zoom,
      view,
    } = useSelector((state: RootState) => state.bluBookingStudioCalendar);
    const dispatch = useDispatch();

    const { sessionProfilo, focus10 } = getConfig();
    const scheduleObj = useRef<ScheduleComponent>(null);

    const getCellStatus = useCallback(
      (date: Date, type: string): CellStatus => {
        const calendar = calendarPointOfSale;
        const shift = shiftPointOfSale;
        if (!calendar && !shift)
          return { code: 0, color: 'var(--closed-color)' };
        console.log('getCellStatus', { date, type, calendar, shift });
        const time = dayjs(date).format('HH:mm:ss');
        const dateKey = dayjs(date).format('YYYY-MM-DD');
        const check = (cal: any) =>
          cal &&
          ((time >= cal.startTime1 && time < cal.endTime1) ||
            (time >= cal.startTime2 && time < cal.endTime2));

        const calendarWorkHours =
          calendar && dateKey in calendar ? calendar[dateKey] : undefined;
        const shiftWorkHours =
          shift && dateKey in shift ? shift[dateKey] : undefined;

        if (type === 'monthCells') {
          if (calendarWorkHours)
            return {
              code: 2,
              color: 'var(--base-foreground-color)',
            };
          if (shift) return { code: 1, color: 'var(--alt-background-color)' };
          return { code: 0, color: 'var(--closed-color)' };
        }
        console.log('calendarWorkHours', calendarWorkHours);
        console.log('shiftWorkHours', shiftWorkHours);

        if (check(calendarWorkHours))
          return {
            code: 2,
            color: 'var(--base-foreground-color)',
          };
        if (check(shiftWorkHours))
          return { code: 1, color: 'var(--alt-background-color)' };
        return { code: 0, color: 'var(--closed-color)' };
      },
      [calendarPointOfSale, shiftPointOfSale]
    );

    const cellTemplate = useCallback(
      (props: any) => (
        <WorkHoursCellTemplate
          date={props.date}
          type={props.type}
          groupIndex={props.groupIndex}
          getStatus={getCellStatus}
        />
      ),
      [getCellStatus]
    );

    // ─── Syncfusion lifecycle ──────────────────────────────────────
    // Verticale e Orizzontale si alternano senza rimontare: alla persistenza
    // serve la vista, per l'asse delle ore
    useSchedulerPersistence(scheduleObj, {
      calendario: 'studio',
      selectedDate,
      focus10,
      startHour,
      endHour,
      view,
    });
    // La fascia Presenze cambia l'altezza del calendario: accesa, spenta, cambio di
    // modalità o chip che arrivano e vanno a capo.
    useSchedulerLayoutRefresh(scheduleObj, '-studio-schedule-persistence');

    const onActionComplete = (args: any) => {
      if (args.requestType === 'dateNavigate') {
        const schedulerInstance = scheduleObj.current;
        const newDate = schedulerInstance?.selectedDate;
        if (newDate) dispatch(StudioCalendarSetSelectedDate(newDate));
      }
    };

    function dateHeaderTemplate(props: any) {
      const date: string = dayjs(props.date).format('ddd D MMMM YYYY');

      return (
        <div
          style={{
            position: 'fixed',
            width: '100%',
            marginTop: '-10px',
            height: 'fit-content',
          }}
        >
          <div
            style={{
              width: 'fit-content',
              fontWeight: 'bold',
              paddingLeft: '10px',
            }}
          >
            <Typography
              fontSize="14px"
              fontWeight="500"
              sx={{ color: 'unset' }}
            >{`${date.charAt(0).toUpperCase()}${date.slice(1)}`}</Typography>
          </div>
        </div>
      );
    }

    const getWorkDaysStartEndHours = useCallback(
      (args: RenderCellEventArgs) => {
        if (
          args.elementType === 'workCells' ||
          args.elementType === 'monthCells'
        ) {
          const studiList = Object.values(studi).filter(
            (studio: any) => studio.pointOfSaleId === selectedPointOfSale?.id
          );
          if (args?.groupIndex !== undefined) {
            args.element.setAttribute(
              'studioid',
              studiList[args.groupIndex]?.id
            );
          }
          args.element.id =
            args.date !== null
              ? dayjs(args.date).format('DD/MM/YYYY HH:mm') ?? ''
              : '';
          args.element.addEventListener('mousedown', AppointmentMouseDown, {
            capture: true,
          });
          args.element.addEventListener(
            'mouseup',
            (event: any) => AppointmentMouseUp(event, 'studio'),
            {
              capture: true,
            }
          );
        } else if (args.elementType === 'majorSlot') {
          args.element.setAttribute(
            'style',
            'font-size:14px; font-weight: 500;'
          );
        }
      },
      [selectedPointOfSale?.id, studi]
    );

    const getZoom = useCallback(() => {
      return {
        enable: true,
        interval: zoom?.interval,
        slotCount: zoom?.slotCount,
      };
    }, [zoom]);

    const getModules = useMemo(() => {
      if (!sessionProfilo.agendaModifica) return [Day, TimelineViews];
      return [Day, DragAndDrop, Resize, TimelineViews];
    }, [sessionProfilo.agendaModifica]);

    return (
      <ScheduleComponent
        id="-studio-schedule-persistence"
        ref={scheduleObj}
        cssClass={`studio${
          view === 'Day' ? '-day' : ''
        }-schedule-cell-dimension`}
        width="100%"
        height="100%"
        timeScale={getZoom()}
        selectedDate={selectedDate}
        currentView={view as View}
        dateFormat="dd/MM/yyyy"
        enablePersistence={true}
        locale="it"
        minDate={new Date(2000, 0, 1)}
        actionComplete={onActionComplete}
        eventSettings={{
          dataSource: appointments,
          template: (props: any) => <CalendarEventTemplate {...props} />,
        }}
        popupOpen={(args) =>
          appointmentFormPopupOpen(
            args,
            selectedUsers,
            [args.data.studioId],
            selectedServices,
            selectedPointOfSale?.id
          )
        }
        dragStop={(args: any) =>
          AppointmentDragDrop(args, appointments, 'studi')
        }
        resizeStop={(args: any) => AppointmentResize(args, appointments)}
        firstDayOfWeek={1}
        renderCell={getWorkDaysStartEndHours}
        cellTemplate={cellTemplate}
        startHour={formatOraSyncfusion(startHour)}
        endHour={formatOraSyncfusion(endHour)}
        eventRendered={(args) => AppointmentRender(args)}
        dateHeaderTemplate={dateHeaderTemplate}
        group={{ byDate: true, resources: ['Studio'] }}
        resourceHeaderTemplate={(props: any) => {
          return (
            <div className="template-wrap">
              <div className="resource-detail">
                <div
                  className="resource-name"
                  style={{ height: 'fit-content', paddingLeft: '10px' }}
                >
                  {props.resourceData.name}
                </div>
              </div>
            </div>
          );
        }}
        quickInfoTemplates={
          {
            header: (_: any) => null,
            content: (props: any) => <CalendarEventQuickInfo {...props} />,
          } as any
        }
      >
        <ResourcesDirective>
          <ResourceDirective
            field="studioId"
            title="Studi"
            name="Studio"
            allowMultiple={true}
            dataSource={studi}
            textField="name"
            idField="studioId"
            colorField="color"
            workDaysField="workDays"
            startHourField="startHour"
            endHourField="endHour"
          />
        </ResourcesDirective>
        <ViewsDirective>
          <ViewDirective option="Day" />
          <ViewDirective option="TimelineDay" />
        </ViewsDirective>
        <Inject services={getModules} />
      </ScheduleComponent>
    );
  }
);
